package com.htmlagent.site.components.widgets

import androidx.compose.runtime.Stable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import com.htmlagent.shared.session.ImageUpload
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import kotlinx.coroutines.await
import org.w3c.dom.CanvasRenderingContext2D
import org.w3c.dom.HTMLCanvasElement
import org.w3c.files.File
import org.w3c.files.FileList
import kotlin.js.Promise
import kotlin.math.max
import kotlin.math.min
import kotlin.math.roundToInt

/** How many photos one message takes; the backend refuses a fifth. */
const val MAX_REFERENCE_PHOTOS = 4

/** The longest side a model looks at: anything larger is resized on its end and billed anyway. */
private const val MAX_SIDE = 1568

/** The backend's ceiling per photo, in bytes. */
private const val MAX_BYTES = 1_572_864

/** Base64 spends four characters on every three bytes. */
private const val BASE64_CHARS = 4
private const val BASE64_BYTES = 3

/** First try, and the one used if the first comes out too heavy. */
private val QUALITIES = listOf(0.85, 0.7)

/**
 * A picked or pasted file turned into what travels with the message: at most [MAX_SIDE] pixels a
 * side, as JPEG. Shrinking here and not on the server is what keeps the request small and the
 * turn cheap. The white fill keeps a transparent PNG from turning black in a JPEG.
 *
 * `null` when the browser cannot decode it, or it will not fit under the ceiling.
 */
suspend fun shrinkForReference(file: File): ImageUpload? = try {
    val bitmap = window.asDynamic().createImageBitmap(file).unsafeCast<Promise<dynamic>>().await()
    val width = bitmap.width as Int
    val height = bitmap.height as Int
    val scale = min(1.0, MAX_SIDE.toDouble() / max(width, height))

    val canvas = document.createElement("canvas") as HTMLCanvasElement
    canvas.width = max(1, (width * scale).roundToInt())
    canvas.height = max(1, (height * scale).roundToInt())
    val context = canvas.getContext("2d") as CanvasRenderingContext2D
    context.fillStyle = "#ffffff"
    context.fillRect(0.0, 0.0, canvas.width.toDouble(), canvas.height.toDouble())
    context.asDynamic().drawImage(bitmap, 0, 0, canvas.width, canvas.height)
    bitmap.close()

    QUALITIES.asSequence()
        .map { quality -> canvas.toDataURL("image/jpeg", quality).substringAfter(",") }
        .firstOrNull { base64 -> base64.length / BASE64_CHARS * BASE64_BYTES <= MAX_BYTES }
        ?.let { ImageUpload(mediaType = "image/jpeg", base64 = it) }
} catch (cancellation: CancellationException) {
    throw cancellation
} catch (_: Throwable) {
    null
}

/**
 * The photos waiting in the composer, and why the last one was not taken if it was not. Picked,
 * pasted and dropped files all come in through [add]; past the fourth none is taken. [tooMany]
 * and [unreadable] are the texts for those two refusals, in the interface's language.
 */
@Stable
class PendingPhotos(
    private val scope: CoroutineScope,
    private val tooMany: String,
    private val unreadable: String,
) {

    var photos by mutableStateOf<List<ImageUpload>>(emptyList())
        private set

    var error by mutableStateOf<String?>(null)
        private set

    val full: Boolean get() = photos.size >= MAX_REFERENCE_PHOTOS

    fun add(files: List<File>) {
        val images = files.filter { it.type.startsWith("image/") }
        if (images.isEmpty()) return
        val room = (MAX_REFERENCE_PHOTOS - photos.size).coerceAtLeast(0)
        error = if (images.size > room) tooMany else null
        scope.launch {
            images.take(room).forEach { file ->
                val shrunk = shrinkForReference(file)
                if (shrunk == null) error = unreadable else if (!full) photos = photos + shrunk
            }
        }
    }

    fun remove(index: Int) {
        photos = photos.filterIndexed { i, _ -> i != index }
        error = null
    }

    /** Hands the photos over to the message being sent and empties the tray. */
    fun take(): List<ImageUpload> = photos.also {
        photos = emptyList()
        error = null
    }
}

/** The image files in a paste, a drop or a picker, in the order they came. */
fun FileList?.imageFiles(): List<File> =
    if (this == null) emptyList() else (0 until length).mapNotNull(::item).filter { it.type.startsWith("image/") }

/** The photo as a `data:` URL, to paint it before the server has it. */
fun ImageUpload.dataUrl(): String = "data:$mediaType;base64,$base64"
